import { ApolloProvider } from '@apollo/client'
import { type IdToken, useAuth0 } from '@auth0/auth0-react'
import {
  getOrganizationIdFromPath,
  setPartialAuth0ResponseToLocalStorage,
  Tracker,
  useApolloClient,
  useSalescoreAuth,
} from '@salescore/client-base'
import { Loading } from '@salescore/client-common'
import { logger } from '@salescore/frontend-common'
import { useNavigate } from '@tanstack/react-router'
import { type ReactElement, useEffect, useState } from 'react'

const redirectAfterLogin = 'redirectAfterLogin'

const Body = ({ token, children }: { token: string; children: ReactElement }): ReactElement => {
  const navigate = useNavigate()

  useEffect(() => {
    const redirect = sessionStorage.getItem(redirectAfterLogin)
    sessionStorage.removeItem(redirectAfterLogin)
    if (redirect === null) return

    // オープンリダイレクト脆弱性対策
    const isValidRedirect = redirect.startsWith('/') && !redirect.startsWith('//') && !redirect.includes('\\')
    if (!isValidRedirect) {
      void navigate({ to: '/', replace: true })
      return
    }

    const redirectPath = redirect.split('?')[0]?.split('#')[0]
    // /sign_in と /o/{organizationId}/sign_in の両方をチェック
    // eslint-disable-next-line require-unicode-regexp
    const isSignInPath = redirectPath === '/sign_in' || /^\/o\/[^/]+\/sign_in$/.test(redirectPath ?? '')
    const to = isSignInPath ? '/' : redirect
    void navigate({ to, replace: true })
  }, [navigate])

  const client = useApolloClient(token)

  if (client === undefined) {
    return <></>
  }

  Tracker.shared().setToken(token)

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

const AuthenticatedAuth0ApolloProvider = ({ children }: { children: ReactElement }): ReactElement => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims } = useAuth0()
  const navigate = useNavigate()
  const [idToken, setIdToken] = useState<IdToken>()

  useEffect(() => {
    if (idToken === undefined) {
      return
    }

    setPartialAuth0ResponseToLocalStorage({
      email: idToken.email,
      email_verified: idToken.email_verified,
      exp: idToken.exp,
      updated_at: idToken.updated_at,
      family_name: idToken.family_name,
      given_name: idToken.given_name,
      name: idToken.name,
      nickname: idToken.nickname,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      sub: (idToken as unknown as { sub: string }).sub,
    })
  }, [idToken])

  useEffect(() => {
    const f = async (): Promise<void> => {
      if (isAuthenticated) {
        await getAccessTokenSilently()
        const idToken = await getIdTokenClaims()
        setIdToken(idToken)
      }
    }
    void f()
  }, [isAuthenticated, isLoading])

  if (isLoading) {
    return <Loading message={`認証中...`} />
  }

  if (!isAuthenticated) {
    logger.debug(`Authentication failed on Auth0. Redirect to /sign_in from ${location.href}`)
    sessionStorage.setItem(redirectAfterLogin, location.pathname + location.search + location.hash)

    const oid = getOrganizationIdFromPath()
    if (oid === '') {
      void navigate({ to: '/sign_in' })
    } else {
      void navigate({ to: `/o/$organizationId/sign_in`, params: { organizationId: oid } })
    }
    return <Loading message={`認証できませんでした。リダイレクト中...`} />
  }

  if (idToken?.__raw === undefined) {
    return <Loading message={`アクセストークンの取得中...`} />
  }

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 対応方法が分からなかった

  return <Body token={idToken.__raw}>{children}</Body>
}

export const AuthenticatedApolloProvider = ({ children }: { children: ReactElement }): ReactElement => {
  const { token } = useSalescoreAuth()
  if (token !== null && token !== '') {
    return <Body token={token}>{children}</Body>
  }

  return <AuthenticatedAuth0ApolloProvider>{children}</AuthenticatedAuth0ApolloProvider>
}
